import type { StatusKind } from '@munifocus/ui';
import {
  NB_COUNTY_KEY,
  isConfiguredBylawCategory,
  type BylawCategory,
  type FileStage,
  type FileStatus,
} from '@munifocus/contracts-gov';
import i18next from 'i18next';

/**
 * Render a coded label alongside the free-text the officer supplied for `other`
 * or `unknown` — "Other (Noise by-law 2019-04)".
 *
 * Read-only views show the code on its own otherwise, which is exactly the
 * uninformative "Other" the companion field exists to fix. Appending rather
 * than replacing keeps the coded value visible, so the display still matches
 * what reporting groups on.
 */
export function withSpecify(label: string, other: string | null | undefined): string {
  return other ? `${label} (${other})` : label;
}

/**
 * Display label for a stored county. Falls back to the raw value when it isn't
 * one of the 15 NB counties: a miss is expected rather than exceptional — files
 * created before the county picker hold whatever the officer transcribed off
 * the PAN record — so we show that text rather than a missing-key placeholder.
 */
export function countyLabel<T extends string | null | undefined>(
  county: T,
  t: (key: string) => string,
): T | string {
  if (!county) return county;
  const key = NB_COUNTY_KEY[county];
  return key ? t(`gov.county.${key}`) : county;
}

/** Map a file's workflow status onto the design-system StatusChip palette. */
export function statusChipKind(status: FileStatus): StatusKind {
  switch (status) {
    case 'emergency':
      return 'emergency';
    case 'awaiting_external':
    case 'paused':
      return 'warning';
    case 'closed':
      return 'success';
    case 'active':
    default:
      return 'pending';
  }
}

/** The ordered stages shown in the file stepper (terminal `closed` excluded). */
export const WORKABLE_STAGES: FileStage[] = [
  'intake',
  'review',
  'site_visit',
  'classification',
  'escalation',
];

export function stageLabelKey(stage: FileStage): string {
  return `gov.stages.${stage}`;
}

/**
 * The statutory provision each stage hangs off, surfaced discreetly in the UI
 * per the design brief (officers can glance and know which section applies).
 * Plain references, no links — see docs/gov/instructions_webapp_development.md.
 */
/**
 * The statutory envelope of the whole Dangerous & Unsightly Premises workflow,
 * surfaced on the file-overview screen (which spans every stage rather than
 * hanging off one). Mirrors the intake reference — the file's governing range.
 */
export const DU_WORKFLOW_REF = 'LGA ss. 131–143';

export const STAGE_LEGISLATIVE_REF: Record<FileStage, string> = {
  intake: 'LGA ss. 131–143',
  review: 'LGA s. 144(2)',
  site_visit: 'LGA s. 144(7)–(8)',
  classification: 'LGA s. 131(1)–(3)',
  escalation: 'LGA s. 137',
  closed: '',
};

/**
 * The stage's D&U citation, or nothing for a configured by-law type: those
 * stages answer to the type's own by-law, which its workflow definition cites,
 * and printing the Local Governance Act beside a noise file would be wrong.
 */
export function stageLegislativeRef(category: BylawCategory | undefined, stage: FileStage): string {
  if (isConfiguredBylawCategory(category)) return '';
  return STAGE_LEGISLATIVE_REF[stage] ?? '';
}

/**
 * A statutory reference in the reader's language. The references are written
 * once, English-style ("LGA s. 137"); a French screen cites the same provision
 * as "LGL, art. 137", the form the French copy already uses (Loi sur la
 * gouvernance locale). Anything that is not an LGA section passes through.
 */
export function localizeStatuteRef(
  ref: string,
  language: string | undefined = i18next.language,
): string {
  if (!ref || !language?.startsWith('fr')) return ref;
  return ref.replace(/\bLGA ss?\. /g, 'LGL, art. ').replace(/(^|[\s/])ss?\. /g, '$1art. ');
}

export function statusLabelKey(status: FileStatus): string {
  return `gov.status.${status}`;
}

/**
 * Is this stored complainant name really "no name"?
 *
 * Intake used to fill an absent name with the English words "Anonymous" or
 * "Internal report"; it now stores an empty string. Both readings are treated
 * as absent so older complaints render in the reader's language too.
 */
export function isPlaceholderName(name: string | null | undefined): boolean {
  const trimmed = name?.trim() ?? '';
  return trimmed === '' || trimmed === 'Anonymous' || trimmed === 'Internal report';
}
